import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import ClearIcon from "@mui/icons-material/Clear";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";

import AddTechnologyToPortfolio from "@components/Forms/AddTechnologyToPortfolio";
import CustomSelect from "@components/Forms/FormInputs/CustomSelect";
import PageTitle from "@components/PageTitle/PageTitle";
import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarSearch,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";
import TabGroup from "@components/Tabs/TabGroup";
import TabPanel from "@components/Tabs/TabPanel";

import { OrganizationUsersType } from "@interfaces/organization.type";

import OrganizationSettings from "@pages/Organizations/components/OrganizationSettings";

import { useOrganizationUsersQuery } from "@queries/organization.query";
import { useCreateTechnologyFromPortfolio } from "@queries/portfolio.query";

import { OrganizationStore, useOrganizationStore } from "@store/organizationStore";
import { PopupActions } from "@store/popupStore";

import contentCardStyles from "@theme/contentCardStyles";

import PageContainer from "../../components/PageContainer";
import PortfolioTable from "../../components/Portfolio/PortfolioTable";

import UsersTable from "./components/UsersTable";

const Organizations = () => {
  // TODO: Move useOrganizationStore into react query instead of storing a local state and having an organization zustand store
  const [portfolioQuery, setPortfolioQuery] = useState("");
  const [usersQuery, setUsersQuery] = useState("");
  const { mutateAsync: createTechnologyFromPortfolio } = useCreateTechnologyFromPortfolio();
  const { organizationId } = useParams();
  const parameters = useParams();
  const { actions: OrganizationActions, organization } = useOrganizationStore();
  const selectedAssignees = OrganizationActions.getSelectedAssignees(); // Access selectedAssignees from store
  const [organizationUsers, setLocalOrganizationUsers] = useState<OrganizationUsersType>([]);
  const tabs = useMemo(() => [{ label: "Technologies" }, { label: "Users" }, { label: "Settings" }], []);

  const [tabIndex, setTabIndex] = useState(() => {
    const foundIndex = tabs.findIndex((tab) => tab.label.toLowerCase() === parameters.tabIdx);
    return Math.max(foundIndex, 0);
  });

  // TODO: switch logic from tabs to Routes. THis will allow us to gate settings and users using route protection.
  const handleTabSwitch = (index: number) => {
    setTabIndex(index);
  };

  // Fetch organization users when organizationId changes
  const { data: organizationUsersData, status } = useOrganizationUsersQuery(organizationId ?? "");

  useEffect(() => {
    if (organizationUsersData && status === "success") {
      setLocalOrganizationUsers(organizationUsersData); // Store fetched users locally
      OrganizationActions.setOrganizationUsers(organizationUsersData); // Update the global store
    }
  }, [organizationUsersData, status, OrganizationActions]);

  const createTechnologySubmit = async (name: string, description: string) => {
    await createTechnologyFromPortfolio({ description, name });
  };

  const openAddTechnologyModal = () => {
    PopupActions.openModal({
      content: <AddTechnologyToPortfolio createTechnologySubmit={createTechnologySubmit} />,
      title: "Create New Technology",
    });
  };

  return (
    <PageContainer>
      <Box sx={contentCardStyles}>
        <Box alignItems="center" display="flex">
          <PageTitle subtitle="" title={organization?.teamName ?? ""} />
        </Box>
        <Box>
          <TabGroup index={tabIndex} setIndex={handleTabSwitch} tabs={tabs} />
        </Box>
        <TabPanel currentIndex={tabIndex} index={0}>
          <TableToolbar>
            <TableToolbarLeft>
              <TableToolbarTitle resourceLink="" tableSubtitle="" tableTitle="Technologies" workflow="" />
            </TableToolbarLeft>
            <TableToolbarRight>
              <TableToolbarSearch
                placeholder="Search Technologies"
                query={portfolioQuery}
                setQuery={setPortfolioQuery}
              />
              <CustomSelect
                clearIcon={<ClearIcon />}
                customRenderValue={(selected) =>
                  organizationUsers
                    .filter((user) => selected.includes(user.userAccountId))
                    .map((user) => `${user.firstName} ${user.lastName}`)
                    .join(", ")
                }
                defaultSelectedOptions={[]}
                formControlSx={{
                  alignContent: "center",
                  m: 1,
                  width: 256,
                }}
                getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
                getOptionValue={(user) => user.userAccountId}
                inputLabel="Filter assignee(s)"
                onChange={OrganizationStore.getState().actions.setSelectedAssignees}
                options={organizationUsers}
                selectedValues={selectedAssignees}
                // TODO: Standardize border radius, width and other toolbar styles so that we are not tracking borderRadius in multiple places.
                sx={{ borderRadius: "8px" }}
              />
              <Button
                color="secondary"
                onClick={openAddTechnologyModal}
                startIcon={<AddCircleIcon />}
                sx={{ borderRadius: "8px", bottom: "1px", position: "relative" }}
                variant="contained"
              >
                New Technology
              </Button>
            </TableToolbarRight>
          </TableToolbar>
          <PortfolioTable
            isOrganization={true}
            organizationId={organizationId}
            searchQuery={portfolioQuery}
            selectedAssignees={selectedAssignees}
          />
        </TabPanel>
        <TabPanel currentIndex={tabIndex} index={1}>
          <TableToolbar>
            <TableToolbarLeft>
              <TableToolbarTitle resourceLink="" tableSubtitle="" tableTitle="Users" workflow="" />
            </TableToolbarLeft>
            <TableToolbarRight>
              <TableToolbarSearch placeholder="Search Users" query={usersQuery} setQuery={setUsersQuery} />
            </TableToolbarRight>
          </TableToolbar>
          {organizationId && (
            <UsersTable organizationId={organizationId} searchQuery={usersQuery} usersData={organizationUsers} />
          )}
        </TabPanel>
        <TabPanel currentIndex={tabIndex} index={2}>
          <OrganizationSettings />
        </TabPanel>
      </Box>
    </PageContainer>
  );
};

export default Organizations;
